/* eslint-disable max-len */
import { useField, useFormikContext } from 'formik';
import { find } from 'lodash';
import { ReactElement, useEffect, useState } from 'react';
import { fetchCourseCodeFormViewModelFullGradeDescriptions } from 'src/redux/thunks/commonThunks';
import { fetchStudentInterventionListItems } from 'src/redux/thunks/studentInterventionThunks';
import { abortPromiseOnUnmount } from 'src/services/base.service';
import { CourseCodeFormViewModel } from 'src/types/CourseCodeFormViewModel';
import { StudentInterventionFormViewModel } from 'src/types/StudentInterventionFormViewModel';
import { StudentInterventionListItemModel } from 'src/types/StudentInterventionListItemModel';
import FormDropdown from '../../../components/FormInputs/FormDropdown/FormDropdown';
import FormRadioList from '../../../components/FormInputs/FormRadioList/FormRadioList';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectCourseCodeFormViewModel } from '../../../redux/slices/commonSlice';
import {
  selectSchoolYear,
  selectStudentInterventionDataEntryMode,
  selectStudentInterventionFormViewModel,
  selectStudentInterventionListItems,
  selectStudentInterventionStudentIntervention,
} from '../../../redux/slices/studentInterventionSlice';
import { isValidCode } from '../../../services/base.service';
import { getStudentByMSISId } from '../../../services/studentIntervention.service';
import { ButtonSize } from '../../../types/ButtonSize';
import { DataEntryMode } from '../../../types/DataEntryMode';
import { PanelType } from '../../../types/PanelType';
import { FormRadioListOrientation } from '../../../types/propTypes/FormRadioListPropTypes';
import { StudentInterventionModel } from '../../../types/StudentInterventionModel';
import { StudentInterventionMsisidSearchResultModel } from '../../../types/StudentInterventionMsisidSearchResultModel';
import { StudentInterventionValidationSchema } from '../../../types/Validation/StudentInterventionValidationSchema';
import ActionButton from '../../ActionButton/ActionButton';
import FormDateSelector from '../../FormInputs/FormDateSelector/FormDateSelector';
import FormTextbox from '../../FormInputs/FormTextbox/FormTextbox';
import Panel from '../../Panel/Panel';
import { MAX_DATE } from 'src/types/Constants';

const StudentIntervention_StudentInterventionPanel = (): ReactElement => {
  const { values, setFieldValue, setFieldTouched, dirty } =
    useFormikContext<StudentInterventionModel>();
  const dispatch = useAppDispatch();
  const [isRaceRequired, setIsRaceRequired] = useState(true);

  useEffect(() => {
    const promise = dispatch(
      fetchCourseCodeFormViewModelFullGradeDescriptions()
    );
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  const formViewModel: CourseCodeFormViewModel = useAppSelector(
    selectCourseCodeFormViewModel
  );

  const getRaceRequiredLabel = (): string => {
    return isRaceRequired ? 'forced-required-label' : '';
  };

  const [, msisIdMetaProps] = useField('studentIdentifierState');
  const getIsMsisIDValid = (): boolean => !msisIdMetaProps?.error;

  const dataEntryMode: DataEntryMode = useAppSelector(
    selectStudentInterventionDataEntryMode
  );

  const listItem: StudentInterventionListItemModel[] = useAppSelector(
    selectStudentInterventionListItems
  );

  const formView: StudentInterventionFormViewModel = useAppSelector(
    selectStudentInterventionFormViewModel
  );

  const studentInterventionInput: StudentInterventionModel = useAppSelector(
    selectStudentInterventionStudentIntervention
  );
  const newModeSchoolYear = useAppSelector(selectSchoolYear);

  const hasMatch: boolean =
    dataEntryMode === DataEntryMode.EDIT || values.studentFirstName?.length > 0;

  const [searching, setSearching] = useState(false);
  const [hasSearchMatch, setHasSearchMatch] = useState(hasMatch);
  const [searchMessage, setSearchMessage] = useState('');

  useEffect(() => {
    let promise: unknown;
    let schoolYear = '';

    if (DataEntryMode.NEW) {
      schoolYear = newModeSchoolYear || formView.defaultSchoolYear;
    } else {
      schoolYear = studentInterventionInput.schoolYear;
    }

    if (schoolYear) {
      promise = dispatch(fetchStudentInterventionListItems(schoolYear));
    }
    return () => {
      abortPromiseOnUnmount(promise);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (dataEntryMode === DataEntryMode.EDIT) {
      if (studentInterventionInput.ethnicity === 'Hispanic or Latino') {
        setIsRaceRequired(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[dispatch]);

  useEffect(() => {
    if (
      (dataEntryMode === DataEntryMode.NEW ||
        dataEntryMode === DataEntryMode.EDIT) &&
      values.studentIdentifierState !== ''
    ) {
      const listItemRecord: StudentInterventionListItemModel | undefined = find(
        listItem,
        {
          studentIdentifierState: values.studentIdentifierState,
          // schoolYear: values.schoolYear, // uncomment this once second panel with lea information is developed
        }
      );

      if (
        dirty &&
        // studentInterventionInput.schoolYear !== values.schoolYear && // uncomment this line once second panel is developed.
        listItemRecord !== undefined
      ) {
        setSearchMessage(
          'A student with this MSIS ID already has an entry.  Please check the MSIS ID and try again.'
        );
      } else {
        setSearchMessage('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.studentIdentifierState]);

  const searchDisabled =
    dataEntryMode === DataEntryMode.EDIT ||
    !getIsMsisIDValid() ||
    searching ||
    hasSearchMatch ||
    searchMessage !== '';

  const handleMSISSearch = async (): Promise<void> => {
    const setUserFields = (
      data: StudentInterventionMsisidSearchResultModel
    ): void => {
      let schoolYear = '';
      if (DataEntryMode.NEW) {
        schoolYear = newModeSchoolYear || formView.defaultSchoolYear;
      } else {
        schoolYear = studentInterventionInput.schoolYear;
      }

      setFieldValue('studentFirstName', data.firstName, true);
      setFieldValue('studentLastOrSurname', data.lastName, true);
      /* istanbul ignore next */
      setFieldValue('studentMiddleName', data.middleName || '', true);
      setFieldValue('birthDate', data.dateOfBirth, true);
      /* istanbul ignore next */
      setFieldValue('studentIdentifierSSN', data.ssn || '', true);
      setFieldValue('districtName', data.districtName, true);
      setFieldValue('districtId', data.districtId, true);
      setFieldValue('schoolYear', schoolYear, true);
      setFieldValue('schoolName', data.schoolName, true);
      setFieldValue('schoolId', data.schoolId, true);
      /* istanbul ignore next */
      setFieldValue('grade', data.grade || '', true);
      setFieldValue('gender', data.gender), true;
      setFieldValue('ethnicity', data.ethnicity, true);
      setFieldValue('race', data.race, true);
      /* istanbul ignore next */
      setFieldValue('sliIndicator', data.sliIndicator, true);

      setFieldValue('hasMatch', true);

      if (data.ethnicity === 'Hispanic or Latino') {
        setIsRaceRequired(false);
      }
    };

    setSearching(true);
    setHasSearchMatch(false);
    setSearchMessage('');

    const response = await getStudentByMSISId(values.studentIdentifierState);
    if (isValidCode(response.status)) {
      setUserFields(response.data);
      setHasSearchMatch(true);
    } else if (response.status == 409) {
      setSearchMessage(
        'This student is not enrolled in the selected/assigned LEA and a school you are assigned to. Please check the MSIS ID and try again.'
      );
    } else {
      // ANY Error Code
      setSearchMessage(
        'MSIS ID Not Found. Please ensure the MSIS ID has been created for the student.'
      );
    }

    setSearching(false);
  };

  const handleMsisValueChange = (): Promise<void> => {
    const resetMatchedFields = (): void => {
      setFieldValue('studentFirstName', '', true);
      setFieldValue('studentLastOrSurname', '', true);
      setFieldValue('studentMiddleName', '', true);
      setFieldValue('birthDate', '', true);
      setFieldValue('studentIdentifierSSN', '', true);

      setFieldValue('districtName', '', true);
      setFieldValue('districtId', '', true);
      setFieldValue('schoolYear', '', true);
      setFieldValue('schoolName', '', true);
      setFieldValue('schoolId', '', true);
      /* istanbul ignore next */
      setFieldValue('grade', '', true);
      setFieldValue('gender', '', true);
      setFieldValue('ethnicity', '', true);
      setFieldValue('race', '', true);
      /* istanbul ignore next */
      setFieldValue('sliIndicator', '', true);

      setFieldValue('hasMatch', false);

      setFieldTouched('studentFirstName', false);
      setFieldTouched('studentLastOrSurname', false);
      setFieldTouched('birthDate', false);
      setFieldTouched('districtName', false);
      setFieldTouched('districtId', false);
      setFieldTouched('schoolYear', false);
      setFieldTouched('schoolId', false);
      setFieldTouched('schoolName', false);
      setFieldTouched('grade', false);
      setFieldTouched('gender', false);
      setFieldTouched('ethnicity', false);
      setFieldTouched('race', false);

      setIsRaceRequired(true);
    };

    setSearchMessage('');

    // Change after match
    if (hasSearchMatch) {
      resetMatchedFields();
      setHasSearchMatch(false);
    }

    return Promise.resolve();
  };

  return (
    <>
      <Panel panelType={PanelType.INFO} heading="Student Intervention">
        <div className="field-row field-row-msis-id">
          <div>
            <FormTextbox
              displayName="Enter MSIS ID"
              field="studentIdentifierState"
              validationSchema={StudentInterventionValidationSchema}
              disabled={dataEntryMode === DataEntryMode.EDIT}
              onChangeEvent={handleMsisValueChange}
            />
          </div>
          <div className="search-button">
            <ActionButton
              classes="button--secondary"
              size={ButtonSize.SMALL}
              onClick={handleMSISSearch}
              disabled={searchDisabled}
              dataTestId="student-search-button"
              tooltipText="Click to search for student"
              loading={searching}
            >
              Search
            </ActionButton>
          </div>
          <div className="search-message" data-testid="search-error-message">
            {searchMessage}
          </div>
        </div>

        <div className="field-row field-row-msis-search-results">
          <FormTextbox
            displayName="Last Name"
            field="studentLastOrSurname"
            disabled={true}
            validationSchema={StudentInterventionValidationSchema}
          />
          <FormTextbox
            displayName="First Name"
            field="studentFirstName"
            disabled={true}
            validationSchema={StudentInterventionValidationSchema}
          />
          <FormTextbox
            displayName="Middle Name"
            field="studentMiddleName"
            disabled={true}
          />
          <FormDateSelector
            displayName="Date of Birth"
            field="birthDate"
            placeholderText=""
            disabled={true}
            validationSchema={StudentInterventionValidationSchema}
            maxDate={MAX_DATE}
            isClearable
          />
          <FormTextbox
            displayName="SSN"
            field="studentIdentifierSSN"
            disabled={true}
          />
        </div>
      </Panel>

      <Panel panelType={PanelType.INFO} heading="LEA & School Information">
        <div className="field-row field-row-msis-search-results">
          <FormTextbox
            displayName="LEA"
            field="districtName"
            validationSchema={StudentInterventionValidationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="LEA Identifier"
            field="districtId"
            validationSchema={StudentInterventionValidationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="School Year"
            field="schoolYear"
            validationSchema={StudentInterventionValidationSchema}
            disabled={true}
          />
        </div>
        <div className="field-row field-row-msis-search-results">
          <FormTextbox
            displayName="School"
            field="schoolName"
            validationSchema={StudentInterventionValidationSchema}
            disabled={true}
          />
          <FormTextbox
            displayName="School Identifier"
            field="schoolId"
            validationSchema={StudentInterventionValidationSchema}
            disabled={true}
          />
          <FormDropdown
            displayName="Grade Level"
            field="grade"
            options={formViewModel.scedGradeSpanOptions}
            validationSchema={StudentInterventionValidationSchema}
            disabled={true}
          />
        </div>
      </Panel>

      <Panel panelType={PanelType.INFO} heading="Student Demographics">
        <div className="field-row field-row-msis-search-results">
          <FormDropdown
            displayName="Gender"
            field="gender"
            validationSchema={StudentInterventionValidationSchema}
            options={[
              { text: 'Male', value: 'Male' },
              { text: 'Female', value: 'Female' },
              { text: 'Not Selected', value: 'Not Selected' },
            ]}
            disabled={true}
          />
          <FormDropdown
            displayName="Ethnicity"
            field="ethnicity"
            validationSchema={StudentInterventionValidationSchema}
            options={[
              { text: 'Hispanic or Latino', value: 'Hispanic or Latino' },
              {
                text: 'Not Hispanic or Latino',
                value: 'Not Hispanic or Latino',
              },
            ]}
            disabled={true}
          />
          <FormDropdown
            displayName="Race"
            field="race"
            validationSchema={StudentInterventionValidationSchema}
            disabled={true}
            options={[
              {
                text: 'American Indian Or Alaska Native',
                value: 'American Indian Or Alaska Native',
              },
              {
                text: 'Asian',
                value: 'Asian',
              },
              {
                text: 'Black Or African American',
                value: 'Black Or African American',
              },
              {
                text: 'White',
                value: 'White',
              },
              {
                text: 'Native Hawaiian Or Other Pacific Islander',
                value: 'Native Hawaiian Or Other Pacific Islander',
              },
              {
                text: 'Demographic Race Two Or More Races',
                value: 'Demographic Race Two Or More Races',
              },
            ]}
            classConfig={{
              labelClasses: getRaceRequiredLabel(),
            }}
          />
          {dataEntryMode === DataEntryMode.EDIT && (
            <FormRadioList
              displayName="SLI Indicator"
              field="sliIndicator"
              options={[
                { text: 'Yes', value: 'Yes' },
                { text: 'No', value: 'No' },
              ]}
              optionOrientation={FormRadioListOrientation.Horizontal}
              validationSchema={StudentInterventionValidationSchema}
              disabled={true}
            />
          )}
        </div>
      </Panel>
    </>
  );
};

export default StudentIntervention_StudentInterventionPanel;
